import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    users: [],
    search: '',
    sortBy: '',
    sortOrder: '',
    commands_filter: ''
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties()
  }
}

function formDefaults () {
  return {
    send_methods: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param users
 * @param original_users
 *
 * @return Object
 */
function transformPermissionsUpdate (users, original_users) {
  let result = []
  // Only build up a list of permissions that has changed
  Object.keys(users).forEach(user_key => {
    const { command_permissions: commands } = users[user_key]
    Object.keys(commands).forEach(command_key => {
      let current = users[user_key].command_permissions[command_key].sending_methods_allowed
      let original = original_users[user_key].command_permissions[command_key].sending_methods_allowed
      // Match sending_methods_allowed with original
      // Check if both arrays are empty
      if (current.length === 0 && original.length === 0) return

      if (current.length === original.length) {
        let matches = true
        // Check that the content is the same.
        current.forEach(value => {
          if (!original.includes(value)) {
            matches = false
          }
        })

        if (!matches) {
          result.push({
            unit_command_id: command_key,
            user_id: user_key,
            sending_methods_allowed: current
          })
        }

        return
      }

      if (current.length > original.length || current.length < original.length) {
        result.push({
          unit_command_id: command_key,
          user_id: user_key,
          sending_methods_allowed: current
        })
      }
    })
  })

  return { permissions: result }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param user_id
 * @param users
 * @param original_users
 *
 * @return Object
 */
function transformLimitsUpdate (user_id, users, original_users) {
  let result = []
  if (user_id) {
    result = [
      {
        user_id: user_id,
        sending_method_limits: users[user_id].sending_method_limits
      }
    ]
    return { limits: result }
  }

  // Only get limits that have changed
  Object.keys(users).forEach(user_key => {
    let current = users[user_key].sending_method_limits
    let original = original_users[user_key].sending_method_limits

    let allowed = false
    Object.keys(users[user_key].command_permissions).forEach(command_key => {
      if (users[user_key].command_permissions[command_key].sending_methods_allowed.length > 0) {
        allowed = true
      }
    })

    if (allowed) {
      Object.keys(current).forEach(send_method_key => {
        if (current[send_method_key].hourly_limit !== original[send_method_key].hourly_limit ||
          current[send_method_key].daily_limit !== original[send_method_key].daily_limit) {
          result.push({
            user_id: user_key,
            sending_method_limits: current
          })
        }
      })
    }
  })

  return { limits: result }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: {
    users: []
  },
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getOriginal (state) {
    return state.original
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  savePermissions (context) {
    const users = context.getters['get'].users
    const original_users = context.getters['getOriginal'].users
    return ApiService
      .put(`unit/commands/permissions`, transformPermissionsUpdate(users, original_users))
      .then((response) => {
        context.commit('setOriginalUsers', JSON.parse(JSON.stringify(users)))
        context.commit('setIsModified', false)
        return response
      })
  },

  saveUserLimits (context, { data, params }) {
    const user_id = params.hasOwnProperty('user_id') ? params.user_id : null
    const users = context.getters['get'].users
    const original_users = context.getters['getOriginal'].users

    return ApiService
      .put(`unit/commands/limits`, transformLimitsUpdate(user_id, users, original_users))
      .then((response) => {
        context.commit('setOriginalUsers', JSON.parse(JSON.stringify(users)))
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`unit/commands/permissions`, params)
      .then((response) => {
        context.commit('setUsers', response.data.data.users)
        context.commit('setSendMethods', response.data.data.send_methods)
        context.commit('setOriginalUsers', JSON.parse(JSON.stringify(response.data.data.users)))
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  setUsers (state, data) {
    state.data.users = Object.assign({}, data)
  },

  setSendMethods (state, data) {
    state.form.send_methods = Object.assign([], data)
  },

  setUnitModelsData (state, data) {
    state.form.unit_models = data
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginalUsers (state, data) {
    state.original.users = Object.assign({}, data)
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  setSearchParams (state, { name, value }) {
    state.data[name] = value
  },

  setCommandFilter (state, { name, value }) {
    state.data[name] = value
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  resetUsers (state) {
    state.data.users = []
    // Make a deep copy of the original data
    state.data.users = JSON.parse(JSON.stringify(state.original.users))
    state.isModified = false
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/units/commands/commandPermissionsModule.js